import { Button, Dropdown, Field, Option, makeStyles } from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { useMemo, useState } from 'react';

import { ErrorState, LoadingState, PageHeader, SectionCard } from '@/design-system';
import { spacing } from '@/theme/tokens';

import { useIncidents } from './api';
import type { IncidentStateMode } from '@/api/types';
import { IncidentTable } from './components/IncidentTable';

const SEVERITIES = [
  { value: undefined, label: 'All severities' },
  { value: 0, label: 'Sev 0 — Critical' },
  { value: 1, label: 'Sev 1 — High' },
  { value: 2, label: 'Sev 2 — Medium' },
  { value: 3, label: 'Sev 3 — Low' },
  { value: 4, label: 'Sev 4 — Informational' },
];

const STATES: { label: string; value: IncidentStateMode }[] = [
  { label: 'Active', value: 'Active' },
  { label: 'All states', value: 'All' },
  { label: 'New', value: 'New' },
  { label: 'Acknowledged', value: 'Acknowledged' },
  { label: 'Closed', value: 'Closed' },
];

const useStyles = makeStyles({
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.s,
    marginBottom: spacing.l,
    alignItems: 'end',
  },
  refreshRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: spacing.s,
  },
  count: {
    fontSize: '12px',
    marginBottom: spacing.s,
  },
});

export function IncidentsPage() {
  const classes = useStyles();
  const [severityFilter, setSeverityFilter] = useState<number | undefined>(undefined);
  const [stateFilter, setStateFilter] = useState<IncidentStateMode>('Active');

  const { data, isLoading, isError, refetch, isFetching } = useIncidents({
    severity: severityFilter,
    state: stateFilter,
  });

  const incidents = useMemo(() => data?.items ?? [], [data]);

  return (
    <div>
      <PageHeader
        title="Incidents"
        description="Azure Monitor alert firings for the selected environment. Synced every 5 minutes. 90-day retention."
      />

      <div className={classes.refreshRow}>
        <Button
          icon={<ArrowClockwiseRegular />}
          appearance="subtle"
          size="small"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <SectionCard>
        <div className={classes.filters}>
          <Field label="Severity">
            <Dropdown
              placeholder="All severities"
              value={
                severityFilter !== undefined
                  ? (SEVERITIES.find((s) => s.value === severityFilter)?.label ?? 'All severities')
                  : 'All severities'
              }
              selectedOptions={severityFilter !== undefined ? [String(severityFilter)] : []}
              onOptionSelect={(_, d) => {
                const v = d.optionValue;
                if (v === undefined || v === 'all') {
                  setSeverityFilter(undefined);
                } else {
                  const n = Number(v);
                  setSeverityFilter(isNaN(n) ? undefined : n);
                }
              }}
            >
              {SEVERITIES.map((s) => (
                <Option key={s.value ?? 'all'} value={s.value !== undefined ? String(s.value) : 'all'}>
                  {s.label}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="State">
            <Dropdown
              placeholder="Active"
              value={STATES.find((state) => state.value === stateFilter)?.label ?? 'Active'}
              selectedOptions={[stateFilter]}
              onOptionSelect={(_, d) => {
                const state = STATES.find((option) => option.value === d.optionValue)?.value;
                if (state) setStateFilter(state);
              }}
            >
              {STATES.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>

        {isLoading ? (
          <LoadingState label="Loading incidents…" />
        ) : isError ? (
          <ErrorState description="Failed to load incidents." retry={() => void refetch()} />
        ) : (
          <>
            <div className={classes.count}>
              {incidents.length} incident{incidents.length !== 1 ? 's' : ''}
              {data?.continuation ? ' (showing first page)' : ''}
              {' — columns can be resized, but server page order is retained.'}
            </div>
            <IncidentTable incidents={incidents} />
          </>
        )}
      </SectionCard>
    </div>
  );
}
